import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { Router, ActivatedRoute } from '@angular/router';
import { ModuleService, Module, UpdateModuleRequest } from '../../../services/module.service';
import { MenuGroupService } from '../../../services/menu-group.service';
import { catchError, finalize } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { FeathericonsModule } from '../../../icons/feathericons/feathericons.module';

@Component({
    selector: 'app-edit-module-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatCardModule,
        FeathericonsModule,
    ],
    templateUrl: './edit-module-page.component.html',
    styleUrl: './edit-module-page.component.scss'
})
export class EditModulePageComponent implements OnInit {
    moduleForm: FormGroup;
    isLoading = false;
    isLoadingModule = false;
    moduleId: string | null = null;
    module: Module | null = null;
    availableIcons: string[] = [];
    menuGroups: { nombre: string; orden: number }[] = [];

    constructor(
        // eslint-disable-next-line no-unused-vars
        private fb: FormBuilder,
        // eslint-disable-next-line no-unused-vars
        private moduleService: ModuleService,
        // eslint-disable-next-line no-unused-vars
        private menuGroupService: MenuGroupService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        this.moduleForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(100)]],
            description: ['', [Validators.maxLength(255)]],
            route: ['', [Validators.maxLength(100)]],
            icon: ['', [Validators.maxLength(50)]],
            order: [0, [Validators.required, Validators.min(0)]],
            menuGroup: ['', [Validators.maxLength(50)]],
            isActive: [true]
        });
    }

    ngOnInit(): void {
        this.initializeAvailableIcons();
        this.loadMenuGroups();
        this.moduleId = this.route.snapshot.paramMap.get('id');
        if (this.moduleId) {
            this.loadModule();
        } else {
            this.snackBar.open('ID de módulo no válido', 'Cerrar', {
                duration: 5000,
                panelClass: ['error-snackbar']
            });
            const queryParams = this.route.snapshot.queryParams;
            this.router.navigate(['/modules'], { queryParams });
        }
    }

    private loadMenuGroups(): void {
        this.menuGroupService.getMenuGroups().subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.menuGroups = res.data
                        .slice()
                        .sort((a, b) => a.orden - b.orden)
                        .map((g) => ({ nombre: g.nombre, orden: g.orden }));
                    this.menuGroups.push({ nombre: 'ADMINISTRACIÓN', orden: 9999 });
                }
            },
        });
    }

    /**
     * Inicializa la lista de íconos disponibles en formato kebab-case
     * basándose en los íconos definidos en FeathericonsModule
     */
    private initializeAvailableIcons(): void {
        const iconNames = [
            'activity', 'camera', 'heart', 'lock', 'table', 'send', 'thumbs-up', 'edit',
            'github', 'align-left', 'alert-triangle', 'file-minus', 'crosshair', 'twitch',
            'plus', 'youtube', 'file', 'folder', 'trash', 'link', 'meh', 'facebook', 'twitter',
            'user-plus', 'codepen', 'smile', 'settings', 'linkedin', 'dribbble', 'x',
            'shopping-bag', 'octagon', 'map-pin', 'command', 'file-text', 'copy', 'flag',
            'list', 'share-2', 'play-circle', 'user-check', 'book-open', 'key', 'search',
            'check', 'coffee', 'code', 'star', 'headphones', 'moon', 'briefcase',
            'message-square', 'users', 'map', 'bar-chart-2', 'info', 'calendar', 'loader',
            'alert-circle', 'more-vertical', 'minimize', 'bell', 'phone', 'edit-3', 'clock',
            'tag', 'globe', 'trash-2', 'dollar-sign', 'percent', 'database', 'check-circle',
            'shopping-cart', 'sliders', 'layers', 'user', 'mail', 'pie-chart', 'home',
            'trending-up', 'trending-down', 'log-out', 'truck', 'droplet',
            'mat:local_gas_station'
        ];
        this.availableIcons = iconNames.sort();
    }

    private loadModule(): void {
        if (!this.moduleId) return;

        this.isLoadingModule = true;
        this.moduleService.getModuleById(this.moduleId)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar módulo', error as Error, 'EditModulePageComponent');
                    this.snackBar.open('Error al cargar la información del módulo', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    const queryParams = this.route.snapshot.queryParams;
                    this.router.navigate(['/modules'], { queryParams });
                    return throwError(() => error);
                }),
                finalize(() => {
                    this.isLoadingModule = false;
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    this.module = response.data;
                    this.populateForm();
                } else {
                    this.snackBar.open('Módulo no encontrado', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    const queryParams = this.route.snapshot.queryParams;
                    this.router.navigate(['/modules'], { queryParams });
                }
            });
    }

    private populateForm(): void {
        if (!this.module) return;

        this.moduleForm.patchValue({
            name: this.module.name,
            description: this.module.description || '',
            route: this.module.route || '',
            icon: this.module.icon || '',
            order: this.module.order,
            menuGroup: this.module.menuGroup || null,
            isActive: this.module.isActive !== undefined ? this.module.isActive : true
        });
    }

    getErrorMessage(fieldName: string): string {
        const control = this.moduleForm.get(fieldName);
        if (!control) return '';

        if (control.hasError('required')) {
            return 'Este campo es requerido';
        }

        if (control.hasError('maxlength')) {
            const requiredLength = control.errors?.['maxlength']?.requiredLength;
            return `Máximo ${requiredLength} caracteres`;
        }

        if (control.hasError('min')) {
            return 'El valor debe ser mayor o igual a 0';
        }

        return 'Campo inválido';
    }

    markFormGroupTouched(): void {
        Object.keys(this.moduleForm.controls).forEach(key => {
            const control = this.moduleForm.get(key);
            control?.markAsTouched();
        });
    }

    onSubmit(): void {
        if (this.moduleForm.valid && this.moduleId) {
            this.isLoading = true;
            
            const formData = this.moduleForm.value;
            const moduleData: UpdateModuleRequest = {
                name: formData.name.trim(),
                description: formData.description?.trim() || undefined,
                route: formData.route?.trim() || undefined,
                icon: formData.icon?.trim() || undefined,
                order: formData.order || 0,
                menuGroup: formData.menuGroup === null || formData.menuGroup === undefined 
                ? null 
                : (formData.menuGroup.trim() !== '' ? formData.menuGroup.trim() : null),
                // Asegurar que isActive siempre tenga un valor booleano
                isActive: formData.isActive === true || formData.isActive === false ? formData.isActive : true
            };

            this.moduleService.updateModule(this.moduleId, moduleData)
                .pipe(
                    catchError(error => {
                        this.logger.error('Error al actualizar módulo', error as Error, 'EditModulePageComponent');
                        const errorMessage = error.error?.message || 'Error al actualizar el módulo';
                        this.snackBar.open(errorMessage, 'Cerrar', {
                            duration: 5000,
                            panelClass: ['error-snackbar']
                        });
                        return of(null);
                    }),
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe(response => {
                    if (response && response.success) {
                        // Notificar que los módulos han cambiado
                        this.moduleService.notifyModulesChanged();
                        
                        this.snackBar.open('Módulo actualizado exitosamente', 'Cerrar', {
                            duration: 3000,
                            panelClass: ['success-snackbar']
                        });
                        const queryParams = this.route.snapshot.queryParams;
                        this.router.navigate(['/modules'], { queryParams });
                    }
                });
        } else {
            this.markFormGroupTouched();
        }
    }

    onCancel(): void {
        const queryParams = this.route.snapshot.queryParams;
        this.router.navigate(['/modules'], { queryParams });
    }
}

